class GTMEventManager {
    static instance = new GTMEventManager();

    constructor() {
        this.enabled = false;
    }

    enable(value) {
        this.enabled = value;
        console.log('[GTMEventManager] Enabled:', value);
        if (value) {
            if (! window.dataLayer) {
                window.dataLayer = [];
            }
        }

        return this;
    }

    trackEvent(eventName, data) {
        if (this.enabled) {
            window.dataLayer.push({ ecommerce: null });
            window.dataLayer.push({ event: eventName, ...data });
        }

        return this;
    }

    pageView(viewName, extraProps = {}) {
        const payload = {
            event_name: 'PageView',
            view_name: viewName,
            content_name: viewName,
            ...extraProps,
        };
        console.log('[GTMEventManager] pageView:', { event: 'page_view', ...payload });

        return this.trackEvent('page_view', payload);
    }

    clickButton(props = {}) {
        const payload = {
            event_name: 'ClickButton',
            ...props
        };
        console.log('[GTMEventManager] clickButton:', { event: 'click_button', ...payload });

        return this.trackEvent('click_button', payload);
    }

    contact(method, source, category = 'Support') {
        const payload = {
            event_name: 'Contact',
            method: method,
            contact_type: method,
            contact_source: source,
            content_category: category, // Category of the contact (support, sales, etc.)
            currency: 'EUR',
            value: 0,
        };
        console.log(`[GTMEventManager] contact:`, { event: 'contact', ...payload });

        return this.trackEvent('contact', payload);
    }

    share(method, contentType, context, contentValue) {
        const payload = {
            event_name: 'Share',
            method: method,
            content_type: contentType,
            content_value: contentValue,
            context: context,
            currency: 'EUR',
            value: 0,
        };
        console.log(`[GTMEventManager] share:`, { event: 'share', ...payload });

        return this.trackEvent('share', payload);
    }

    leaveReview(data, product, value = null, user = null) {
        const ecommerce = new this.ecommerce(this);

        const payload = {
            event: 'leave_review',
            review: data.review,
            comment: data.comment ?? '',
            score: parseInt(data.score),
            ...ecommerce._setCart([{ ...product, price: product.seat_price, quantity: product.seats }], value),
            ...this._setUser(user),
        };
        console.log('[GTMEventManager] leaveReview:', { event: 'leave_review', ...payload });

        return this.trackEvent('leave_review', payload);
    }

    _setUser(user) {
        if (!user || ! user.id) {
            return {};
        }

        return {
            user_data: { id: user.id, email: user?.email },
        };
    }

    // Nested ecommerce class
    ecommerce = class ECommerceManager {
        constructor(gtm) {
            this.gtm = gtm;
        }

        reset() {
            console.log('[GTMEventManager] ecommerce - reset');
            if (this.gtm.enabled) {
                window.dataLayer.push({ ecommerce: null });
            }

            return this.gtm;
        }

        viewItem(item, extraProps = {}) {
            const payload = {
                event_name: 'ViewContent',
                content_name: item.name,
                ...this._setCart([{ game: item, price: item.base_price, quantity: 1 }], item.base_price),
                ...extraProps,
            };
            console.log('[GTMEventManager] ecommerce - viewItem', { event: 'view_item', ...payload });

            return this.gtm.trackEvent('view_item', payload);
        }

        selectItem(item, extraProps = {}) {
            const payload = {
                event_name: 'SelectItem',
                ...this._setCart([{ game: item, price: item.base_price, quantity: 1 }], item.base_price),
                ...extraProps,
            };
            console.log('[GTMEventManager] ecommerce - selectItem', { event: 'select_item', ...payload });

            return this.gtm.trackEvent('select_item', payload);
        }

        customizeProduct(item, extraProps = {}) {
            const payload = {
                event_name: 'CustomizeProduct',
                ...this._setCart([{ game: item, price: item.base_price, quantity: 1 }], item.base_price),
                ...extraProps,
            };
            console.log('[GTMEventManager] ecommerce - customizeProduct', { ...payload });

            return this.gtm.trackEvent('customize_product', payload);
        }

        addToCart(item, quantity, price, pointOfSale, extraProps = {}) {
            const payload = {
                event_name: 'AddToCart',
                ...this._setCart([{ game: item, price, quantity }], quantity * price, pointOfSale),
                ...extraProps,
            };
            console.log('[GTMEventManager] ecommerce - addToCart', { event: 'add_to_cart', ...payload });

            return this.gtm.trackEvent('add_to_cart', payload);
        }

        viewCart(cart, extraProps = {}) {
            const payload = {
                event_name: 'ViewCart',
                privatized: cart.booking.privatized,
                booking_datetime: cart.booking.start_at,
                ...this._setCart([{ game: cart.booking.game, price: cart.booking.seat_price, quantity: cart.booking.seats_full_price }], cart.total, cart.booking.point_of_sale),
                ...extraProps,
            };
            if (cart?.coupons && cart.coupons.length > 0) {
                payload.coupon = cart.coupons[0].code
                payload.discount = parseFloat((cart.coupons[0].discountValue / 100).toFixed(2));
                payload.ecommerce.coupon = payload.coupon
                payload.ecommerce.discount = payload.discount;
            }

            console.log('[GTMEventManager] ecommerce - viewCart', { event: 'view_cart', ...payload });

            return this.gtm.trackEvent('view_cart', payload);
        }

        beginCheckout(cart, extraProps = {}) {
            console.log(cart);
            const payload = {
                event_name: 'InitiateCheckout',
                privatized: cart.booking.privatized,
                booking_datetime: cart.booking.start_at,
                num_items: cart.booking.seats_full_price,
                ...this._setCart([{ game: cart.booking.game, price: cart.booking.seat_price, quantity: cart.booking.seats_full_price }], cart.total, cart.booking.point_of_sale),
                ...extraProps,
            };
            if (cart?.coupons && cart.coupons.length > 0) {
                payload.coupon = cart.coupons[0].code
                payload.discount = parseFloat((cart.coupons[0].discountValue / 100).toFixed(2));
                payload.ecommerce.coupon = payload.coupon
                payload.ecommerce.discount = payload.discount;
            }

            console.log('[GTMEventManager] ecommerce - beginCheckout', { event: 'begin_checkout', ...payload });

            return this.gtm.trackEvent('begin_checkout', payload);
        }

        cancelCheckout(cart, extraProps = {}) {
            const payload = {
                event_name: 'CancelCheckout',
                privatized: cart.booking.privatized,
                booking_datetime: cart.booking.start_at,
                num_items: cart.booking.seats_full_price,
                ...this._setCart([{ game: cart.booking.game, price: cart.booking.seat_price, quantity: cart.booking.seats_full_price }], cart.total, cart.booking.point_of_sale),
                ...extraProps,
            };
            if (cart?.coupons && cart.coupons.length > 0) {
                payload.coupon = cart.coupons[0].code
                payload.discount = parseFloat((cart.coupons[0].discountValue / 100).toFixed(2));
                payload.ecommerce.coupon = payload.coupon
                payload.ecommerce.discount = payload.discount;
            }

            console.log('[GTMEventManager] ecommerce - cancelCheckout', { event: 'cancel_checkout', ...payload });

            return this.gtm.trackEvent('cancel_checkout', payload);
        }

        addPaymentInfo(cart, extraProps = {}) {
            const payload = {
                event_name: 'AddPaymentInfo',
                privatized: cart.booking.privatized,
                booking_datetime: cart.booking.start_at,
                ...this._setCart([{ game: cart.booking.game, price: cart.booking.seat_price, quantity: cart.booking.seats_full_price }], cart.total, cart.booking.point_of_sale),
                ...extraProps,
            };
            console.log('[GTMEventManager] ecommerce - addPaymentInfo', { event: 'add_payment_info', ...payload });

            return this.gtm.trackEvent('add_payment_info', payload);
        }

        addCoupon(cart, coupon, extraProps = {}) {
            const payload = {
                event_name: 'AddCoupon',
                coupon: coupon.code,
                privatized: cart.booking.privatized,
                booking_datetime: cart.booking.start_at,
                discount: parseFloat((coupon.discountValue / 100).toFixed(2)),
                ...this._setCart([{ game: cart.booking.game, price: cart.booking.seat_price, quantity: cart.booking.seats_full_price }], cart.total, cart.booking.point_of_sale),
                ...extraProps,
            };

            payload.ecommerce.coupon = payload.coupon
            payload.ecommerce.discount = payload.discount;

            console.log('[GTMEventManager] ecommerce - addCoupon', { event: 'add_coupon', ...payload });

            return this.gtm.trackEvent('add_coupon', payload);
        }

        removeCoupon(cart, coupon, extraProps = {}) {
            const payload = {
                event_name: 'RemoveCoupon',
                coupon: coupon.code,
                privatized: cart.booking.privatized,
                booking_datetime: cart.booking.start_at,
                discount: parseFloat((coupon.discountValue / 100).toFixed(2)),
                ...this._setCart([{ game: cart.booking.game, price: cart.booking.seat_price, quantity: cart.booking.seats_full_price }], cart.total, cart.booking.point_of_sale),
                ...extraProps,
            };

            payload.ecommerce.coupon = payload.coupon
            payload.ecommerce.discount = payload.discount;

            console.log('[GTMEventManager] ecommerce - removeCoupon', { event: 'remove_coupon', ...payload });

            return this.gtm.trackEvent('remove_coupon', payload);
        }

        purchase(cart, extraProps = {}) {
            const payload = {
                event_name: 'CompletePayment',
                transaction_id: cart.booking.id,
                privatized: cart.booking.privatized,
                booking_datetime: cart.booking.start_at,
                num_items: cart.booking.seats_full_price,
                ...this._setCart([{ game: cart.booking.game, price: cart.booking.seat_price, quantity: cart.booking.seats_full_price }], cart.total, cart.booking.point_of_sale),
                ...extraProps,
            };
            if (cart?.coupons && cart.coupons.length > 0) {
                payload.coupon = cart.coupons[0].code
                payload.discount = parseFloat((cart.coupons[0].discountValue / 100).toFixed(2));
                payload.ecommerce.coupon = payload.coupon
                payload.ecommerce.discount = payload.discount;
            }

            console.log('[GTMEventManager] ecommerce - purchase', { event: 'purchase', ...payload });

            return this.gtm.trackEvent('purchase', payload);
        }

        _setCart(products, value, pointOfSale = null) {
            const payload = {
                currency: 'EUR',
                value: parseFloat((value / 100).toFixed(2)),
                ...this._createProps(products, value),
                ecommerce: {
                    currency: 'EUR',
                    value: parseFloat((value / 100).toFixed(2)),
                    items: products.map((product) => this._createItem(product.game, product.price, product.quantity)),
                },
            }

            if (pointOfSale) {
                payload.ecommerce.items.map((i) => {
                    i.affiliation = pointOfSale.name;
                    i.location_id = 'ChIJkRqevKFv5kcRprsI8CvrxLE'; // TODO: add Google Place ID in database

                    return i;
                });
            }

            return payload;
        }

        _createItem(item, price, quantity = 1) {
            return {
                item_id: item.slug,
                item_internal_id: item.id,
                item_name: item.name,
                item_category: item.genre?.['en']?.toLowerCase(),
                item_list_id: 'all_games',
                item_list_name: 'All games',
                price: parseFloat((price / 100).toFixed(2)),
                quantity: quantity,
                type: item.type,
                is_new: item.new,
                is_popular: item.popular,
            };
        }

        _createProps(products) {
            return {
                content_type: 'product',
                content_name: products[0].game.name,
                content_category: products[0].game?.genre?.['en']?.toLowerCase(),
                content_ids: products.map((product) => product.game.slug),
                contents: products.map((product) => this._createContent(product.game, product.price, product.quantity)),
            }
        }

        _createContent(item, price, quantity = 1) {
            return {
                id: item.slug,
                item_id: item.id,
                price: parseFloat((price / 100).toFixed(2)),
                quantity: quantity,
                title: item.name,
                category: item.genre?.['en']?.toLowerCase(),
                type: item.type,
                is_new: item.new,
                is_popular: item.popular,
            };
        }
    };

    // Static methods as entry points
    static get e() {
        return GTMEventManager.instance;
    }

    static enable(value) {
        return GTMEventManager.instance.enable(value);
    }

    static trackEvent(eventName, data) {
        return GTMEventManager.instance.trackEvent(eventName, data);
    }

    static pageView(viewName, extraProps = {}) {
        return GTMEventManager.instance.pageView(viewName, extraProps);
    }

    static clickButton(props = {}) {
        return GTMEventManager.instance.clickButton(props);
    }

    static contact(method, source) {
        return GTMEventManager.instance.contact(method, source);
    }

    static share(method, contentType, context, contentValue) {
        return GTMEventManager.instance.share(method, contentType, context, contentValue);
    }

    static leaveReview(data, product, value, user = null) {
        return GTMEventManager.instance.leaveReview(data, product, value, user);
    }

    static ecommerce() {
        return new GTMEventManager.instance.ecommerce(GTMEventManager.instance);
    }
}

export default GTMEventManager;
